import * as React from 'react';

import {ACTION_CONFIG, CHAT_ACTION} from 'shells/chat/consts';
import {DraggableList} from 'shells/draggable-row/draggable-list';
import type {ListItem} from 'shells/draggable-row/draggable-list';
import {reorder} from 'shells/draggable-row/helpers';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {Switch} from 'shells/switch/switch';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {ActionBarActionEnum, ChatVariantEnum} from 'nutshell-graphql-types';
import type {ChatSettingsLookAndFeelInput, ChatActionsFragment} from 'nutshell-graphql-types';

import {CHAT_VARIANT, SAVE_STATE_KEYS} from '../consts';
import type {SaveStateKey} from '../consts';

import {ActionOptionRow} from './action-option-row';

type Props = {
    chatVariant: ChatVariantEnum;
    setChatVariant: (chatVariant: ChatVariantEnum) => void;
    actionBarShownActions: ActionBarActionEnum[] | null | undefined;
    actionBarHiddenActions: ActionBarActionEnum[] | null | undefined;
    actions?: ChatActionsFragment | null | undefined;
    handleSave: (updates: ChatSettingsLookAndFeelInput, saveStateKey: SaveStateKey) => Promise<any>;
    toastState: SavedSettingToastState;
    savingToastKey: SaveStateKey | null | undefined;
    isLoading: boolean;
};

const MAX_VISIBLE_ACTIONS = 5;

export function EngagementBarActionsSection(props: Props) {
    const {handleSave} = props;
    const [shownActionIds, setShownActionIds] = React.useState<
        ActionBarActionEnum[] | null | undefined
    >(null);
    const [hiddenActionIds, setHiddenActionIds] = React.useState<
        ActionBarActionEnum[] | null | undefined
    >(null);

    React.useEffect(() => {
        if (props.isLoading || !props.actionBarShownActions || !props.actionBarHiddenActions) {
            return;
        }

        setShownActionIds(props.actionBarShownActions);
        setHiddenActionIds(props.actionBarHiddenActions);
    }, [props.isLoading, props.actionBarShownActions, props.actionBarHiddenActions]);

    const handleToggleAction = React.useCallback(
        (actionId: ActionBarActionEnum) => {
            // Chat action cannot be toggled - it must always be on when engagement bar is enabled
            if (!shownActionIds || !hiddenActionIds || actionId === CHAT_ACTION) {
                return;
            }

            let newShownActionIds;
            let newHiddenActionIds;

            if (shownActionIds.includes(actionId)) {
                // Move from shown to hidden
                newShownActionIds = shownActionIds.filter((id) => id !== actionId);
                newHiddenActionIds = [...hiddenActionIds, actionId];
            } else if (hiddenActionIds.includes(actionId)) {
                // Move from hidden to shown
                if (shownActionIds.length >= MAX_VISIBLE_ACTIONS) {
                    return;
                }

                newHiddenActionIds = hiddenActionIds.filter((id) => id !== actionId);
                newShownActionIds = [...shownActionIds, actionId];
            } else {
                return;
            }

            setShownActionIds(newShownActionIds);
            setHiddenActionIds(newHiddenActionIds);

            handleSave(
                {
                    actionBarShownActions: newShownActionIds,
                    actionBarHiddenActions: newHiddenActionIds,
                },
                SAVE_STATE_KEYS.ACTION_BAR_ACTIONS
            );
        },
        [shownActionIds, hiddenActionIds, handleSave]
    );

    const handleShownActionsReorder = (startIndex: number, endIndex: number) => {
        if (!shownActionIds) {
            return;
        }

        const newShownActionIds = reorder([...shownActionIds], startIndex, endIndex);
        setShownActionIds(newShownActionIds);

        props.handleSave(
            {
                actionBarShownActions: newShownActionIds,
            },
            SAVE_STATE_KEYS.ACTION_BAR_ACTIONS
        );
    };

    const handleHiddenActionsReorder = (startIndex: number, endIndex: number) => {
        if (!hiddenActionIds) {
            return;
        }

        const newHiddenActionIds = reorder([...hiddenActionIds], startIndex, endIndex);
        setHiddenActionIds(newHiddenActionIds);

        props.handleSave(
            {
                actionBarHiddenActions: newHiddenActionIds,
            },
            SAVE_STATE_KEYS.ACTION_BAR_ACTIONS
        );
    };

    const createListItem = React.useCallback(
        (actionId: ActionBarActionEnum, isShown: boolean, isToggleDisabled: boolean): ListItem => {
            let isConfigured = true;

            const config = ACTION_CONFIG[actionId];
            if (props.actions) {
                const fragmentData = props.actions[config.fragmentKey];
                isConfigured = fragmentData.isConfigured;
            }

            return {
                id: actionId,
                component: (
                    <ActionOptionRow
                        actionId={actionId}
                        label={config.label}
                        icon={config.icon}
                        handleToggle={() => handleToggleAction(actionId)}
                        isOn={isShown}
                        isConfigured={isConfigured}
                        toggleDisabled={isToggleDisabled}
                    />
                ),
            };
        },
        [handleToggleAction, props.actions]
    );

    const shownActions = React.useMemo(() => {
        if (!shownActionIds) {
            return null;
        }

        // Chat action toggle is always disabled - chat must be on when engagement bar is enabled
        return shownActionIds.map((id) => createListItem(id, true, id === CHAT_ACTION));
    }, [shownActionIds, createListItem]);

    const hiddenActions = React.useMemo(() => {
        if (!hiddenActionIds || !shownActionIds || !props.actions) {
            return null;
        }

        return hiddenActionIds
            .map((id) => {
                const isToggleDisabled =
                    id === CHAT_ACTION || shownActionIds.length >= MAX_VISIBLE_ACTIONS;
                const item = createListItem(id, false, isToggleDisabled);
                const config = ACTION_CONFIG[id];
                // @ts-expect-error
                const fragmentData = props.actions[config.fragmentKey];

                return {
                    ...item,
                    isConfigured: fragmentData.isConfigured,
                };
            })
            .sort((a, b) => {
                if (!a.isConfigured && b.isConfigured) return 1;
                if (a.isConfigured && !b.isConfigured) return -1;

                return 0;
            });
    }, [hiddenActionIds, shownActionIds, props.actions, createListItem]);

    return (
        <Stack spacing={16}>
            <SavedSettingToast
                state={
                    props.savingToastKey === SAVE_STATE_KEYS.ACTION_BAR_ACTIONS
                        ? props.toastState
                        : 'initial'
                }
            >
                <Body size='large' isBold={true}>
                    Engagement bar
                </Body>
            </SavedSettingToast>
            <Body>
                The engagement bar lets visitors take actions beyond chat, such as scheduling a
                call, requesting contact, or asking for a quote.
            </Body>
            <LabelWithContent
                label='Show engagement bar'
                state={
                    props.savingToastKey === SAVE_STATE_KEYS.ENGAGEMENT_BAR
                        ? props.toastState
                        : 'initial'
                }
                spacing={8}
                content={
                    props.isLoading ? (
                        <LoadingText isFullWidth={true} height={30} />
                    ) : (
                        <Stack spacing={8}>
                            <Body>
                                Visitors will see all the actions you have enabled along with
                                conversation starters.
                            </Body>
                            <Stack isHorizontal={true} spacing={8}>
                                <Switch
                                    onChange={() => {
                                        const newChatVariant =
                                            props.chatVariant === CHAT_VARIANT.CHAT
                                                ? CHAT_VARIANT.ACTIONS
                                                : CHAT_VARIANT.CHAT;
                                        props.setChatVariant(newChatVariant);
                                        props.handleSave(
                                            {chatVariant: newChatVariant},
                                            SAVE_STATE_KEYS.ENGAGEMENT_BAR
                                        );
                                    }}
                                    isRounded={true}
                                    isOn={props.chatVariant === CHAT_VARIANT.ACTIONS}
                                    isSmall={true}
                                />
                                <Body> {props.chatVariant === 'ACTIONS' ? 'On' : 'Off'}</Body>
                            </Stack>
                        </Stack>
                    )
                }
            />
            <Body>
                Define up to 5 visible actions below, and{''}
                <Body isBold={true}> configure them under the ‘Actions’ tab.</Body>
            </Body>
            {props.isLoading || !shownActions || !hiddenActions ? (
                <LoadingText width={500} height={400} />
            ) : (
                <Stack spacing={16}>
                    <LabelWithContent
                        label='Visible actions'
                        sublabel={`${shownActions.length} / ${MAX_VISIBLE_ACTIONS} slots filled`}
                        isSublabelHorizontal={true}
                        content={
                            <DraggableList
                                listItems={shownActions}
                                handleReorder={handleShownActionsReorder}
                            />
                        }
                    />
                    <LabelWithContent
                        label='Hidden actions'
                        content={
                            <DraggableList
                                listItems={hiddenActions}
                                handleReorder={handleHiddenActionsReorder}
                            />
                        }
                    />
                </Stack>
            )}
        </Stack>
    );
}
